import { Desc, VariableMap } from '@sm/intl';

import {
  CORPORATE_SIGNATURE_RESEARCH_ENDPAGE_COPY,
  CX_ENDPAGE_COPY,
  GO_BEYOND_TEXT_BANNER_COPY,
  MRX_ENDPAGE_COPY,
  RACIAL_JUSTICE_ENDPAGE_COPY,
  POLLING_ENDPAGE_COPY,
  POLLING_TEXT_BANNER_COPY,
  STANDARD_ENDPAGE_DEFAULT_COPY,
  UPENN_POLLING_ENDPAGE_COPY,
} from './copy';

import AustraliaMap from '~static/images/australia_polling.png';
import CanadaMap from '~static/images/canada_map.png';
import cxMaze from '~static/images/cx-maze.png';
import mrxShoe from '~static/images/mrx-shoe.png';
import researchReport from '~static/images/research-report.png';
import rjGroup from '~static/images/rj-group.png';
import ScotlandMap from '~static/images/scotland_polling.png';
import UKMap from '~static/images/uk_election_map_large.png';
import USMap from '~static/images/us_political_map_large.png';
import { BackgroundColorProp } from '~app/components/TextBanner';

type PageProps = {
  imageSource: string;
  hasShadedBackground: boolean;
  logoRoute: string;
  pageCTA: Desc;
  pageCTARoute?: string;
  pageHeading: Desc;
  pageSubheading?: Desc;
  privacyNotice?: [Desc, VariableMap];
  textBannerBackgroundColor: BackgroundColorProp;
  textBannerCTA: Desc;
  textBannerCTARoute: string;
  textBannerHeading?: Desc;
  textBannerSubheading: Desc;
  utSource2: string;
};

export type Page = {
  [key: string]: PageProps;
};

const Pages: Page = {
  CorporateSignatureResearchEndpage: {
    imageSource: researchReport,

    hasShadedBackground: true,
    logoRoute: '/',
    pageCTA: CORPORATE_SIGNATURE_RESEARCH_ENDPAGE_COPY.PAGE_CTA_TEXT,
    // @todo https://momentiveai.atlassian.net/browse/ANONWEB-5440
    // Before endpages are exposed to non-english locales we will need to localize URLs to some CMS website, see ticket.
    pageCTARoute: '/mp/2020-research-report/',
    pageHeading: CORPORATE_SIGNATURE_RESEARCH_ENDPAGE_COPY.PAGE_HEADING,
    pageSubheading: CORPORATE_SIGNATURE_RESEARCH_ENDPAGE_COPY.PAGE_SUBHEADING,
    textBannerBackgroundColor: 'dark1',
    textBannerCTA: GO_BEYOND_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: 'https://www.momentive.ai/en/',
    textBannerHeading: GO_BEYOND_TEXT_BANNER_COPY.HEADING,
    textBannerSubheading: GO_BEYOND_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'CorporateSignatureResearchEndpage',
  },
  CxEndpage: {
    imageSource: cxMaze,
    hasShadedBackground: true,
    logoRoute: '/',
    pageCTA: CX_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageCTARoute: 'https://www.getfeedback.com/en/start/',
    pageHeading: CX_ENDPAGE_COPY.PAGE_HEADING,
    pageSubheading: CX_ENDPAGE_COPY.PAGE_SUBHEADING,
    textBannerBackgroundColor: 'dark1',
    textBannerCTA: GO_BEYOND_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: 'https://www.momentive.ai/en/',
    textBannerHeading: GO_BEYOND_TEXT_BANNER_COPY.HEADING,
    textBannerSubheading: GO_BEYOND_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'CxEndpage',
  },
  MrxEndpage: {
    imageSource: mrxShoe,
    hasShadedBackground: true,
    logoRoute: '/',
    pageCTA: MRX_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageCTARoute: '/curiosity/how-allbirds-uses-surveymonkey-audience-to-fuel-its-explosive-brand-growth/',
    pageHeading: MRX_ENDPAGE_COPY.PAGE_HEADING,
    pageSubheading: MRX_ENDPAGE_COPY.PAGE_SUBHEADING,
    textBannerBackgroundColor: 'dark1',
    textBannerCTA: GO_BEYOND_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: 'https://www.momentive.ai/en/',
    textBannerHeading: GO_BEYOND_TEXT_BANNER_COPY.HEADING,
    textBannerSubheading: GO_BEYOND_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'MrxEndpage',
  },
  PollingAustraliaMap: {
    imageSource: AustraliaMap,
    hasShadedBackground: true,
    logoRoute: '/',
    pageCTA: POLLING_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageHeading: POLLING_ENDPAGE_COPY.PAGE_HEADING,
    privacyNotice: [
      STANDARD_ENDPAGE_DEFAULT_COPY.PRIVACY_NOTICE,
      // @todo: https://momentiveai.atlassian.net/browse/ANONWEB-5440
      // Before endpages are exposed to non-english locales we will need to localize URLs to some CMS website, see ticket.
      // @todo: add ut_source tracking
      { link: 'https://www.surveymonkey.com/mp/legal/survey-research-privacy-notice/' },
    ],
    textBannerBackgroundColor: 'dark2',
    textBannerCTA: POLLING_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: '/user/sign-up/',
    textBannerSubheading: POLLING_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'PollingAustraliaMap',
  },
  PollingCanadaMap: {
    imageSource: CanadaMap,
    hasShadedBackground: true,
    logoRoute: '/',
    pageCTA: POLLING_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageHeading: POLLING_ENDPAGE_COPY.PAGE_HEADING,
    privacyNotice: [
      STANDARD_ENDPAGE_DEFAULT_COPY.PRIVACY_NOTICE,
      { link: 'https://www.surveymonkey.com/mp/legal/survey-research-privacy-notice/' },
    ],
    textBannerBackgroundColor: 'dark2',
    textBannerCTA: POLLING_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: '/user/sign-up/',
    textBannerSubheading: POLLING_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'PollingCanadaMap',
  },
  PollingScotlandMap: {
    imageSource: ScotlandMap,
    hasShadedBackground: true,
    logoRoute: '/',
    pageCTA: POLLING_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageHeading: POLLING_ENDPAGE_COPY.PAGE_HEADING,
    privacyNotice: [
      STANDARD_ENDPAGE_DEFAULT_COPY.PRIVACY_NOTICE,
      { link: 'https://www.surveymonkey.com/mp/legal/survey-research-privacy-notice/' },
    ],
    textBannerBackgroundColor: 'dark2',
    textBannerCTA: POLLING_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: '/user/sign-up/',
    textBannerSubheading: POLLING_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'PollingScotlandMap',
  },
  PollingUKMap: {
    imageSource: UKMap,
    hasShadedBackground: true,
    logoRoute: '/',
    pageCTA: POLLING_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageHeading: POLLING_ENDPAGE_COPY.PAGE_HEADING,
    privacyNotice: [
      STANDARD_ENDPAGE_DEFAULT_COPY.PRIVACY_NOTICE,
      { link: 'https://www.surveymonkey.com/mp/legal/survey-research-privacy-notice/' },
    ],
    textBannerBackgroundColor: 'dark2',
    textBannerCTA: POLLING_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: '/user/sign-up/',
    textBannerSubheading: POLLING_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'PollingUKMap',
  },
  PollingUPenn: {
    imageSource: USMap,
    hasShadedBackground: true,
    logoRoute: '/',
    pageCTA: POLLING_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageHeading: POLLING_ENDPAGE_COPY.PAGE_HEADING,
    privacyNotice: [
      UPENN_POLLING_ENDPAGE_COPY.PRIVACY_NOTICE,
      {
        UPennPrivacyPolicyLink: 'https://www.upenn.edu/about/privacy-policy',
        SurveyMonkeyPrivacyPolicyLink: 'https://www.surveymonkey.com/mp/legal/privacy/',
      },
    ],
    textBannerBackgroundColor: 'dark2',
    textBannerCTA: POLLING_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: '/user/sign-up/',
    textBannerSubheading: POLLING_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'PollingUPenn',
  },
  PollingUSMap: {
    imageSource: USMap,
    hasShadedBackground: true,
    logoRoute: '/',
    pageCTA: POLLING_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageHeading: POLLING_ENDPAGE_COPY.PAGE_HEADING,
    privacyNotice: [
      STANDARD_ENDPAGE_DEFAULT_COPY.PRIVACY_NOTICE,
      { link: 'https://www.surveymonkey.com/mp/legal/survey-research-privacy-notice/' },
    ],
    textBannerBackgroundColor: 'dark2',
    textBannerCTA: POLLING_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: '/user/sign-up/',
    textBannerSubheading: POLLING_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'PollingUSMap',
  },
  RacialJusticeEndpage: {
    imageSource: rjGroup,
    hasShadedBackground: true,
    logoRoute: '/',
    pageCTA: RACIAL_JUSTICE_ENDPAGE_COPY.PAGE_CTA_TEXT,
    pageCTARoute: '/mp/workforce-racial-equity/',
    pageHeading: RACIAL_JUSTICE_ENDPAGE_COPY.PAGE_HEADING,
    pageSubheading: RACIAL_JUSTICE_ENDPAGE_COPY.PAGE_SUBHEADING,
    textBannerBackgroundColor: 'dark1',
    textBannerCTA: GO_BEYOND_TEXT_BANNER_COPY.CTA_TEXT,
    textBannerCTARoute: 'https://www.momentive.ai/en/',
    textBannerHeading: GO_BEYOND_TEXT_BANNER_COPY.HEADING,
    textBannerSubheading: GO_BEYOND_TEXT_BANNER_COPY.SUBHEADING,
    utSource2: 'RacialJusticeEndpage',
  },
};

export default Pages;
